import { createFileRoute } from "@tanstack/react-router";
import {
  LiveRatesTable,
  LiveChart,
  SarrafiyeCalc,
  TrLinkMatrix,
  FaqAccordion,
  buildTrSchema,
  trDateParts,
  goldRatesQueryOptions,
  useGramTry,
  formatTry,
} from "@/components/tr/shell";

const SLUG = "/tr/il-il-altin-fiyatlari-kuyumcular";
const CANONICAL = `https://drhint.com${SLUG}`;

const FAQS = [
  { q: "İzmir, Bursa ve Konya kuyumcular odası altın fiyatları neden farklıdır?", a: "İzmir Kuyumcular Odası (İZKO), Bursa Kapalıçarşı ve Konya Sarraflar Odası her sabah yerel referans fiyatını Kapalıçarşı serbest piyasa üzerinden %0.3-%1.5 arasında değişen bir yerel makas ekleyerek yayınlar. Yerel makas — nakliye, kasa, işçilik ve yerel talep farkını yansıtır." },
  { q: "Kayseri, Eskişehir ve Elazığ altın piyasası İstanbul'dan neden yüksek olabilir?", a: "İstanbul dışı illerde nakliye maliyeti, düşük hacim ve rafineri erişim mesafesi nedeniyle serbest piyasaya %0.5-%1.5 arası yerel prim eklenir. Örneğin Elazığ ve Malatya kuyumcular odası fiyatları Kapalıçarşı gram altına 30-80 TL/gram arası prim ekleyerek işlem yapar." },
  { q: "Bugün gram altın ne kadar, kaç TL oldu?", a: "Sayfadaki canlı takip tablosundan anlık serbest piyasa gram altın satış değerini Türk Lirası olarak görebilirsiniz. Kendi ilinizin kuyumcu odası fiyatı, bu değere %0.3-%1.5 yerel prim eklenerek hesaplanır." },
];

export const Route = createFileRoute("/tr/il-il-altin-fiyatlari-kuyumcular")({
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
    return null;
  },
  head: () => {
    const dp = trDateParts();
    const title = `İl İl Kuyumcular Odası Altın Fiyatları — Canlı Gram, Çeyrek, Cumhuriyet`;
    const description = `İzmir İZKO, Bursa Kapalı Çarşı, Konya, Kayseri, Eskişehir, Elazığ, Malatya kuyumcular odası anlık gram altın fiyatları TL olarak burada. Yerel primler dahil.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow,max-snippet:-1,max-image-preview:large" },
        { httpEquiv: "content-language", content: "tr-TR" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: CANONICAL },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "tr_TR" },
      ],
      links: [
        { rel: "canonical", href: CANONICAL },
        { rel: "alternate", hreflang: "tr-TR", href: CANONICAL },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildTrSchema({ slug: SLUG, name: title, description, faqs: FAQS })),
        },
      ],
    };
  },
  component: RegionalPage,
});

const CITY_GROUPS: { heading: string; cities: { name: string; premium: number; note: string }[] }[] = [
  {
    heading: "İzmir (İZKO) ve Bursa Kapalı Çarşı Altın Fiyatları",
    cities: [
      { name: "İzmir (İZKO)", premium: 0.004, note: "İzmir Kuyumcular Odası referansı" },
      { name: "Bursa Kapalı Çarşı", premium: 0.005, note: "Bursa sarrafiye tezgahları" },
    ],
  },
  {
    heading: "Konya, Kayseri ve Eskişehir Canlı Altın Piyasası",
    cities: [
      { name: "Konya Sarraflar Odası", premium: 0.006, note: "Konya bölge referansı" },
      { name: "Kayseri Kuyumcular", premium: 0.008, note: "İç Anadolu prim aralığı" },
      { name: "Eskişehir Kuyumcular", premium: 0.007, note: "Eskişehir sarrafiye" },
    ],
  },
  {
    heading: "Elazığ ve Malatya Kuyumcular Odası Fiyat Listesi",
    cities: [
      { name: "Elazığ Kuyumcular Odası", premium: 0.012, note: "Doğu Anadolu prim" },
      { name: "Malatya Kuyumcular Odası", premium: 0.011, note: "Yerel nakliye + kasa primi" },
    ],
  },
];

function RegionalPage() {
  const dp = trDateParts();
  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="mx-auto max-w-6xl px-4 pt-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-amber-500">Türkiye · Kuyumcular Odası</p>
        <h1 className="mt-2 text-2xl md:text-4xl font-black leading-tight">
          İllere Göre Güncel Kuyumcu Altın Fiyatları ({dp.dayNum} {dp.month} {dp.year})
        </h1>
        <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-3xl">
          İzmir'den Elazığ'a, Bursa'dan Konya'ya kadar il il kuyumcular odası fiyat referansları, Kapalıçarşı gram altın satışına yerel prim eklenerek hesaplanır. Aşağıdaki bloklar bölgesel primleri saniye saniye TL üzerinden gösterir.
        </p>
      </header>
      <LiveChart title={`İl İl Gram Altın Grafiği — ${dp.month} ${dp.year}`} />
      <LiveRatesTable />
      {CITY_GROUPS.map((g) => (
        <RegionalGroup key={g.heading} heading={g.heading} cities={g.cities} />
      ))}
      <SarrafiyeCalc />
      <FaqAccordion items={FAQS} />
      <TrLinkMatrix current={SLUG} />
    </main>
  );
}

function RegionalGroup({ heading, cities }: { heading: string; cities: { name: string; premium: number; note: string }[] }) {
  const { gram24 } = useGramTry();
  return (
    <section className="mx-auto max-w-6xl px-4 py-4">
      <h2 className="text-lg md:text-xl font-bold mb-3">{heading}</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cities.map((c) => {
          const price = gram24 * (1 + c.premium);
          return (
            <div key={c.name} className="rounded-2xl border border-amber-500/25 bg-card p-4">
              <div className="font-semibold">{c.name}</div>
              <div className="mt-1 text-lg font-extrabold tabular-nums text-amber-700 dark:text-amber-300">{formatTry(price)} <span className="text-xs font-normal text-muted-foreground">/ gram</span></div>
              <div className="mt-1 text-xs text-muted-foreground">Yerel prim: %{(c.premium * 100).toFixed(1)} · {c.note}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}